import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import {Link as LinkIcon, Save, Loader2, Check, ExternalLink} from 'lucide-react'
import toast from 'react-hot-toast'

const CHAVE_CONFIG = 'link_materiais_divulgacao'

interface ConfigDoc {
  _id: string
  chave: string
  valor: string
  descricao?: string
  createdAt: string
  updatedAt: string
}

export default function LinkMateriaisConfig() {
  const [registroId, setRegistroId] = useState<string | null>(null)
  const [valor, setValor] = useState('')
  const [valorSalvo, setValorSalvo] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    let ativo = true
    ;(async () => {
      try {
        const resp: any = await lumi.entities.configuracoes.list({
          filter: { chave: CHAVE_CONFIG },
          limit: 1,
        })
        const lista: ConfigDoc[] = resp?.list ?? []
        if (!ativo) return
        if (lista.length > 0) {
          setRegistroId(lista[0]._id)
          setValor(lista[0].valor || '')
          setValorSalvo(lista[0].valor || '')
        }
      } catch (e) {
        console.error('Erro ao carregar configuração de materiais:', e)
      } finally {
        if (ativo) setCarregando(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [])

  const salvar = async () => {
    const linkLimpo = valor.trim()
    if (linkLimpo && !/^https?:\/\//i.test(linkLimpo)) {
      toast.error('O link deve começar com http:// ou https://')
      return
    }
    setSalvando(true)
    try {
      const agora = new Date().toISOString()
      if (registroId) {
        await lumi.entities.configuracoes.update(registroId, {
          valor: linkLimpo,
          updatedAt: agora,
        })
      } else {
        const novo: any = await lumi.entities.configuracoes.create({
          chave: CHAVE_CONFIG,
          valor: linkLimpo,
          descricao:
            'Link do Google Drive com os Materiais de Divulgação que aparece para os consultores na aba Como Divulgar',
          creator: 'admin',
          createdAt: agora,
          updatedAt: agora,
        })
        if (novo?._id) setRegistroId(novo._id)
      }
      setValorSalvo(linkLimpo)
      toast.success('Link salvo com sucesso!')
    } catch (e) {
      console.error('Erro ao salvar link:', e)
      toast.error('Erro ao salvar o link. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  const houveAlteracao = valor.trim() !== valorSalvo.trim()

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-verde-escuro animate-spin" />
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      <div className="bg-verde-escuro px-6 py-4">
        <div className="flex items-center gap-3">
          <LinkIcon className="w-5 h-5 text-white flex-shrink-0" />
          <h2 className="text-xl font-cormorant font-semibold text-white">
            Configurações
          </h2>
        </div>
      </div>
      <div className="p-6">
        <div className="mb-5">
          <h3 className="text-lg font-cormorant font-semibold text-texto-escuro mb-2">
            Link dos Materiais de Divulgação
          </h3>
          <p className="text-sm text-texto-suave leading-relaxed">
            Cole aqui o link do Google Drive (ou outra pasta pública) com os materiais
            de apoio à divulgação. Esse link aparecerá para todos os consultores no
            bloco <strong>"Outros Materiais"</strong> da aba <em>Como Divulgar</em>.
          </p>
        </div>

        <label className="block text-sm font-medium text-texto-escuro mb-2">
          Link do Google Drive
        </label>
        <input
          type="url"
          value={valor}
          onChange={e => setValor(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..."
          className="w-full px-4 py-3 rounded-xl border-2 border-bege-medio bg-bege-fundo focus:outline-none focus:border-verde-escuro transition-colors text-sm"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        />

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
          <button
            onClick={salvar}
            disabled={salvando || !houveAlteracao}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {salvando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : houveAlteracao ? (
              <>
                <Save className="w-4 h-4" />
                Salvar link
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Link salvo
              </>
            )}
          </button>

          {valorSalvo && (
            <a
              href={valorSalvo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm border-2 border-verde-escuro text-verde-escuro hover:bg-verde-muito-claro transition-all min-h-[44px]"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir link salvo
            </a>
          )}
        </div>

        {!valorSalvo && (
          <div className="mt-5 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#92400E] leading-relaxed">
            💡 Enquanto o link não for preenchido, o botão <strong>"Acessar Materiais"</strong> da aba
            "Como Divulgar" dos consultores aparecerá desabilitado com aviso de "Em breve".
          </div>
        )}
      </div>
    </div>
  )
}
